/** @module qt.router
 *  @description 常规路由
 */
import { invokeSchemaByPrompt } from '../core';
import { inApp } from '../env';
import { stringifyQuery } from '../utils';
import { compareAppVersion, invokeSchema } from '../base';

export enum SearchTypeEnum {
  ALL = 'all',
  CHANNEL = 'channel',
  POD_CASTER = 'podcaster',
  RADIO = 'radio',
  PROGRAM = 'program',
}
/**
 * 返回上一页，支持站外
 * @example
 * ```javascript
 * qt.route.backTo()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function backTo(): Promise<unknown> {
  if (inApp) {
    return invokeSchema('qingtingfm://action.qingting.fm/clickBack');
  }
  window.history.go(-1);
  return Promise.resolve();
}

/**
 * 打开新的webview
 * @version: 
 * @example
 * ```javascript
 * qt.route.openNewWebview({url: 'https://m.qingting.fm', params: {test: 1}})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function openNewWebview({
  url,
  params = {},
}: {
  /** 跳转链接(无需编码) */
  url: string;
  /** schema 使用的 query 参数对象(仅站内使用 */
  params?: Record<string, string>;
}): Promise<unknown> {
  if (inApp) {
    if (/^(qingtingfm?:\/\/)/.test(url)) {
      window.location.href = url;
      return Promise.resolve(url);
    }
    return compareAppVersion('8.4.2').then(({ isLess }) => {
      if (isLess) {
        const query = stringifyQuery(params);
        window.location.href = `qingtingfm://app.qingting.fm/webview?url=${encodeURIComponent(url)}&${query}`;
        return Promise.resolve();
      }
      const options: Record<string, string> = { url, ...params };
      return invokeSchemaByPrompt('qingtingfm://app.qingting.fm/webview', options);
    });
  }
  window.location.href = url;
  return Promise.resolve(url);
}

/**
 * 跳转首页
 * @deprecated 请使用jumpToTab
 */
export function jumpToHome({
  tabId,
}: {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tabId: string;
}): Promise<unknown> {
  return invokeSchema(`qingtingfm://app.qingting.fm/mainTab/${tabId}`);
}

/**
 * 跳转首页标签
 * @example
 * ```javascript
 * qt.route.jumpToTab({tabId: '5210'})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function jumpToTab({
  type,
  tabId,
}: {
  /** 标签类型，type 与 tabId 只有 1 个生效 */
  type?: string;
  /** 标签 id，即 channelsite */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tabId?: string;
}): Promise<unknown> {
  return compareAppVersion('9.0.6').then(({ isGreaterOrEqual }) => {
    if (isGreaterOrEqual) {
      if (!type && !tabId) return invokeSchema(`qingtingfm://app.qingting.fm/mainTab`);
      if (type) return invokeSchema(`qingtingfm://app.qingting.fm/mainTab?type=${type}`);
      if (tabId) return invokeSchema(`qingtingfm://app.qingting.fm/mainTab?channelsite=${tabId}`);
    } else {
      return jumpToHome({ tabId: tabId || '' });
    }
    return Promise.resolve();
  });
}

/**
 * 跳转搜索页
 */
export function jumpToSearch({
  keyword,
  categoryId,
  type = SearchTypeEnum.ALL,
}: {
  /** 关键字 */
  keyword?: number;
  /** 在此 id 对应的分类下搜索 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  categoryId?: number;
  /** 搜索内容的类型	 */
  type?: SearchTypeEnum;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/search', {
    keyword,
    categoryId,
    type,
  });
}

/**
 * 跳转排行榜
 * @example
 * ```
 * qt.route.jumpToRankList({categoryId:'545'})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function jumpToRankList({
  type = 'hot',
  range = 'weekly',
  categoryId = 0,
}: {
  /** 类型, 默认hot */
  type?: string;
  /** 时间段, 默认weekly */
  range?: string;
  /** 时间段, 默认weekly */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  categoryId?: number;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/rankinglist', {
    type,
    range,
    category: categoryId,
  });
}

/**
 * 跳转推荐页
 * @version: IOS: 8.4.2+; Android: 8.0.4+
 * @example
 * ```
 * qt.route.jumpToRecommend({channelId:'271526'})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function jumpToRecommend({
  channelId,
}: {
  /** 专辑 id */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  channelId: string;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/recommend', { channelId });
}

/**
 * 跳转超级会员界面
 * @example
 * ```
 * qt.route.jumpToSuperVip()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });

 * ```
 */
export function jumpToSuperVip(): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/supervip');
}

/**
 * 跳转兑换页，支持站外
 * @version: IOS: 不支持; Android: 8.0.0+
 * @example
 * ```
 * qt.route.jumpToExchange({code:'1234'})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function jumpToExchange({
  code = '',
  awakeApp,
}: {
  /** 优惠券码 */
  code?: string;
  /** 站外直接唤醒APP而不是跳转  */
  awakeApp?: boolean;
}): Promise<unknown> {
  if (inApp || awakeApp) {
    return invokeSchema('qingtingfm://app.qingting.fm/user/redeem', { code });
  }
  window.location.href = 'https://sss.qingting.fm/exchange/index.html';
  return Promise.resolve();
}

/**
 * 跳转充值页
 * @example
 * ```
 * qt.route.jumpToRecharge()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function jumpToRecharge({
  fromView,
  needAmount,
}: {
  /** 来源 */
  fromView?: string;
  /** 需要充值的数量  */
  needAmount?: number;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/user/qtcoin/charge', {
    from_view: fromView,
    need_amount: needAmount,
  });
}

/**
 * 跳转打包购买页
 * @example
 * ```
 * qt.route.jumpToChannelOrder({
  itemId:'5b7ba09dd0e5d4696569d917',
  itemName:'晓年鉴'
})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function jumpToChannelOrder({
  itemId,
  itemName,
}: {
  /** 打包购买专辑 ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  itemId: string;
  /** 打包购买专辑名称	 */
  itemName?: string;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/channel/ondemand/packagepaid', {
    item_id: itemId,
    item_name: itemName,
  });
}

/**
 * 跳转广告下载
 * @version: IOS: 8.0.9+; Android: 8.0.9+
 * @example
 * ```
 * if (qt.env.inAndroidApp) {
  qt.route.jumpToDownloadAdApp({
    url: 'http://apk.ixiaqi.net/yizhan.apk',
    title: '测试下载'
  }).then(function (res) {
    console.log(res);
  }).catch(function (e) {
    console.log(e);
  });
} else {
  qt.route.jumpToDownloadAdApp({
    url: 'https://itunes.apple.com/cn/app/id1189134199?mt=8'
  }).then(function (res) {
    console.log(res);
  }).catch(function (e) {
    console.log(e);
  });
}
 * ```
 */
export function jumpToDownloadAdApp({
  url,
  title,
  size,
}: {
  /** 下载链接地址，ios是iTunes链接地址 */
  url: string;
  /** APP名字（Android专用 */
  title: string;
  /** 下载包大小  */
  size?: string;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://app.qingting.fm/ad/appdownload', {
    url,
    title,
    size,
  });
}

/** @ignore */
export const common = {
  backTo,
  openNewWebview,
  jumpToHome,
  jumpToTab,
  jumpToSearch,
  jumpToRankList,
  jumpToRecommend,
  jumpToSuperVip,
  jumpToExchange,
  jumpToRecharge,
  jumpToChannelOrder,
  jumpToDownloadAdApp,
};
